// @flow
import {
  compose,
  sort,
  map,
  split,
  flatten,
  times,
  head,
  last,
  apply,
} from 'ramda';
import compareAsc from 'date-fns/compare_asc';
import max from 'date-fns/max';
import min from 'date-fns/min';
import isWithinRange from 'date-fns/is_within_range';
import isEqual from 'date-fns/is_equal';
import isAfter from 'date-fns/is_after';
import addMonths from 'date-fns/add_months';
import addYears from 'date-fns/add_years';
import differenceInYears from 'date-fns/difference_in_years';
import startOfMonth from 'date-fns/start_of_month';
import startOfYear from 'date-fns/start_of_year';
import differenceInCalendarMonths from 'date-fns/difference_in_calendar_months';
import parse from 'date-fns/parse';
import format from 'date-fns/format';
import isValid from 'date-fns/is_valid';

export const sortDates = (a: Date, b: Date) =>
  isEqual(a, b) ? 0 : isAfter(a, b) ? 1 : -1;

/*
 * Constructs iso time interval out of two dates
 */
export const getInterval = (a: Date, b: Date) =>
  `${toISOFormat(a)}/${toISOFormat(b)}`;

/*
 * Just like toISOString but without Z
 */
export const toISOFormat = (date: Date) =>
  format(date, 'YYYY-MM-DDTHH:mm:ss.SSS');

/*
 * Sorts intervals by the first date
 */
export const sortIntervals = (
  a: { timespan: string },
  b: { timespan: string },
) => {
  const [aStart] = a.timespan.split('/');
  const [bStart] = b.timespan.split('/');

  return sortDates(parse(aStart), parse(bStart));
};

/*
 * Returns the length of provided time interval
 * (right border is inclusive)
 */
export const intervalLengthMonths = (interval: string) => {
  const [start, end] = interval.split('/');
  return differenceInCalendarMonths(end, start) + 1;
};

/*
 * Expands provided time interval by years
 */
export const expandIntervalByYears = (interval: string) => {
  const [start, end] = interval.split('/');
  const yearStart = startOfYear(start);
  const diff = differenceInYears(end, yearStart) + 1;

  return times(n => getISOIntervalByLen(addYears(yearStart, n), 12), diff);
};

/*
 * Expands provided interval by months
 */
export const expandIntervalByMonths = (interval: string) => {
  const [start, end] = interval.split('/');
  const diff = differenceInCalendarMonths(end, start);

  const monthStart = startOfMonth(start);

  return times(n => getISOIntervalByLen(addMonths(monthStart, n), 1), diff + 1);
};

/*
 * This function transforms interval from monthly to yearly precision.
 * It ensures, that resulting interval will lie inside outerInterval.
 */
export const convertToYearlyPrecision = (
  interval: string,
  outerInterval: string,
) => {
  const [start, end] = interval.split('/');
  const [minStart, maxEnd] = outerInterval.split('/');
  const newStart = max(
    startOfYear(addMonths(minStart, 11)),
    startOfYear(start),
  );
  const newEnd = min(
    addMonths(startOfYear(addMonths(maxEnd, 1)), -1),
    addMonths(startOfYear(end), 11),
  );
  if (compareAsc(newStart, newEnd) === -1) {
    return getInterval(newStart, newEnd);
  } else if (compareAsc(newEnd, minStart) <= 0) {
    return getInterval(newStart, addMonths(newStart, 11));
  }
  return getInterval(addMonths(newEnd, -11), newEnd);
};

export const previousMonth = (date: Date) => addMonths(date, -1);

/*
 * Add a number of months to a iso time interval
 */
export const addMonthsInterval = (count: number, interval: string) =>
  compose(
    apply(getInterval),
    map(date => addMonths(parse(date), count)),
    split('/'),
  )(interval);

/*
 * Subtracts from time interval it's length and returns the new interval
 */
export const previousPeriod = (interval: string) =>
  addMonthsInterval(-intervalLengthMonths(interval), interval);

/*
 * Shifts time interval one year back
 */
export const oneYearBack = (interval: string) =>
  addMonthsInterval(-12, interval);

/*
 * Builds an array of time intervals inside provided bounds
 */
export const recurring = (bounds: string, interval: string) => {
  const [start] = interval.split('/');
  const [min] = bounds.split('/');

  const count = Math.floor(differenceInYears(start, min));
  return times(n => addMonthsInterval(-12 * (n + 1), interval), count);
};

/*
 * Constructs iso interval by one date and duration in months
 */
export const getISOIntervalByLen = (dateA: Date, monthsCount: number) =>
  getInterval(dateA, addMonths(dateA, monthsCount - 1));

/*
 * Constructs a single time interval out of time interval array
 * that includes all the intervals in the array
 */
export const totalInterval = (intervals: string[]) => {
  const dates = compose(sort(sortDates), flatten, map(split('/')))(intervals);
  return [head(dates), last(dates)].join('/');
};

/*
 * Constructs iso interval by joining the lowest of two intervals
 */
export const lowestInterval = (inner: string, outer: string) => {
  const [innerStart] = inner.split('/');
  const [outerStart] = outer.split('/');
  return getInterval(innerStart, outerStart);
};

/*
 * Converts an interval to an array
 */
export const intervalToArray = (interval: any) =>
  interval && typeof interval === 'string' ? interval.split('/') : [null, null];

/*
 * Is first interval is (non-strictly) inside second
 */
export const intervalInside = (inner: string, outer: string) => {
  if (outer && inner) {
    const [outerStart, outerEnd] = outer.split('/');
    const [innerStart, innerEnd] = inner.split('/');
    return (
      isWithinRange(innerStart, outerStart, outerEnd) &&
      isWithinRange(innerEnd, outerStart, outerEnd)
    );
  } else {
    return false;
  }
};

/*
 * Is interval year or longer
 */
export const yearOrLonger = (interval: string) =>
  intervalLengthMonths(interval) >= 12;

/*
 * Is the interval year or shorter
 */
export const yearOrShorter = (interval: string) =>
  intervalLengthMonths(interval) <= 12;

/*
 * Check if the string is a valid time interval
 */
export const isTimeInterval = (interval: string) => {
  const dates = interval.split('/');
  if (dates.length !== 2) return false;
  return isValid(parse(dates[0])) && isValid(parse(dates[1]));
};

export const isEqualInterval = (first: string, second: string) => {
  const [start1, end1] = first.split('/');
  const [start2, end2] = second.split('/');

  return isEqual(start1, start2) && isEqual(end1, end2);
};



// WEBPACK FOOTER //
// ./src/utils/time.js